import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { breadcrumbSchema } from "@/lib/schema";
import { ClusterLinks } from "@/components/ClusterLinks";
import { TECH_NODES, TECH_CATS, type TechCat, type TechTier } from "@/lib/tech-data";
import { ContextVisualStrip } from "@/components/ContextVisualStrip";

export const Route = createFileRoute("/technology-tree/")({
  head: () => ({
    meta: [
      { title: "Palworld Technology Tree — Unlocks by Level" },
      { name: "description", content: "Full Palworld Technology Tree: every Tech and Ancient Tech unlock from level 1 to 60, with tech points, ancient points, prerequisites and build order." },
      { property: "og:title", content: "Palworld Technology Tree — Pal Stars" },
      { property: "og:description", content: "Every Tech and Ancient Tech unlock from level 1 to 60 with points and prerequisites." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/technology-tree" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld Technology Tree" },
      { name: "twitter:description", content: "All Tech and Ancient Tech unlocks with points and prerequisites." },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/technology-tree" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Technology Tree", "/technology-tree"]]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "Palworld Technology Tree",
          description: "Every Palworld Tech and Ancient Tech unlock indexed by level, cost and prerequisites.",
          url: "https://palstars.com/technology-tree",
          keywords: ["palworld tech tree", "palworld technology tree", "palworld ancient technology", "palworld tech unlocks"],
          creator: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com" },
          license: "https://creativecommons.org/licenses/by-nc/4.0/",
          isAccessibleForFree: true,
        }),
      },
    ],
  }),
  component: TechTreePage,
});

function TechTreePage() {
  const [tier, setTier] = useState<TechTier | "All">("All");
  const [cat, setCat] = useState<TechCat | "All">("All");
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return TECH_NODES.filter((n) =>
      (tier === "All" || n.tier === tier) &&
      (cat === "All" || n.cat === cat) &&
      (!needle || `${n.name} ${n.cat} ${n.note}`.toLowerCase().includes(needle))
    ).sort((a, b) => a.lvl - b.lvl);
  }, [tier, cat, q]);

  const totals = useMemo(() => ({
    tech: rows.filter((r) => r.tier === "Tech").reduce((s, r) => s + r.cost, 0),
    ancient: rows.filter((r) => r.tier === "Ancient").reduce((s, r) => s + r.cost, 0),
  }), [rows]);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/" className="hover:text-foreground">Home</Link> / <span className="text-foreground">Technology Tree</span>
      </nav>
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Technology Tree</h1>
      <p className="aeo-answer mt-2 max-w-2xl text-muted-foreground">
        Every Palworld Tech and Ancient Tech unlock from level 1 to 60. Filter by tier, category, or search — each row links to that unlock's own page with its level gate, point cost and what it enables.
      </p>
      <div className="mt-2 text-xs text-muted-foreground">
        See also the <Link to="/guides/$slug" params={{ slug: "tech-tree" }} className="text-primary hover:underline">Palworld Tech Tree Guide</Link> for build-order strategy.
      </div>
      <ContextVisualStrip variant="technology" />

      <div className="mt-6 flex flex-wrap gap-2 text-xs">
        {(["All", "Tech", "Ancient"] as const).map((t) => (
          <button key={t} onClick={() => setTier(t)} className={`rounded-full border px-3 py-1.5 font-semibold ${tier === t ? "bg-primary text-primary-foreground border-primary" : "hover:bg-card"}`}>{t}</button>
        ))}
        <span className="mx-2 h-6 w-px bg-border" />
        <button onClick={() => setCat("All")} className={`rounded-full border px-3 py-1.5 ${cat === "All" ? "bg-primary text-primary-foreground border-primary" : "hover:bg-card"}`}>All categories</button>
        {TECH_CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`rounded-full border px-3 py-1.5 ${cat === c ? "bg-primary text-primary-foreground border-primary" : "hover:bg-card"}`}>{c}</button>
        ))}
        <label htmlFor="tech-search" className="sr-only">Search tech tree</label>
        <input id="tech-search" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search tech tree" placeholder="Search tech name or effect…" className="min-w-[220px] flex-1 rounded-lg glass px-4 py-2 outline-none focus:ring-2 ring-primary" />
      </div>

      <div className="mt-3 text-xs text-muted-foreground">
        Showing <b>{rows.length}</b> unlocks · Tech Points needed: <b>{totals.tech}</b> · Ancient Tech Points needed: <b>{totals.ancient}</b>
      </div>

      <div className="glass rounded-xl mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Palworld technology unlocks by level, tier, cost and prerequisite</caption>
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
              <th scope="col" className="p-3">Lvl</th>
              <th scope="col" className="p-3">Unlock</th>
              <th scope="col" className="p-3">Tier</th>
              <th scope="col" className="p-3">Category</th>
              <th scope="col" className="p-3">Cost</th>
              <th scope="col" className="p-3">Requires</th>
              <th scope="col" className="p-3">Why it matters</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((n) => (
              <tr key={n.slug} className="border-b hover:bg-card/60">
                <td className="p-3 font-mono">{n.lvl}</td>
                <td className="p-3 font-semibold">
                  <Link to="/technology-tree/$slug" params={{ slug: n.slug }} className="text-primary hover:underline">
                    {n.name}
                  </Link>
                </td>
                <td className="p-3">
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] ${n.tier === "Ancient" ? "border-amber-500/40 text-amber-400" : "border-sky-500/40 text-sky-400"}`}>{n.tier}</span>
                </td>
                <td className="p-3 text-muted-foreground">{n.cat}</td>
                <td className="p-3">{n.cost}</td>
                <td className="p-3 text-muted-foreground">{n.needs ?? "—"}</td>
                <td className="p-3 text-muted-foreground">{n.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <nav aria-label="Related Palworld databases" className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-xl font-bold">Related Palworld databases</h2>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-sm">
          <li><Link to="/items" className="font-semibold text-primary hover:underline">Palworld items and weapons database</Link></li>
          <li><Link to="/passives" className="font-semibold text-primary hover:underline">Palworld passive skills list</Link></li>
          <li><Link to="/mounts" className="font-semibold text-primary hover:underline">Palworld mount speed table</Link></li>
          <li><Link to="/breeding" className="font-semibold text-primary hover:underline">Palworld breeding calculator</Link></li>
        </ul>
      </nav>

      <ClusterLinks hub="/technology-tree" heading="Palworld progression and platform guides" />
    </div>
  );
}
